import { Menu } from '@mantine/core';
import { useApiRemoveEntityFromTeam } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Icon } from '@repo/foundations';
import { useNavigate } from 'react-router';
import type { ISecodaEntity } from '../../../../api';
import {
	queryClient,
	secodaEntitiesQueryKeyFactory,
	useAuthUser,
	useUpdateSecodaEntity,
} from '../../../../api';

import { catalogQueryKeyFactory } from '../../../../api/hooks/catalog/constants';
import { resourceCatalogQueryKeyFactory } from '../../../../api/hooks/resourceCatalog/constants';
import { useCurrentTeam } from '../../../../api/hooks/team/myMemberships';
import { useEntityBreadcrumbTeam } from '../../../../hooks/useEntityBreadcrumbs';
import { resolveBaseEntityPath } from '../../../../utils/navigationUtils';
import { openDeleteConfirmModal } from '../../../ModalManager';

interface DeleteActionProps {
	entity: ISecodaEntity;
}

export function DeleteAction({ entity }: DeleteActionProps) {
	const { currentTeam } = useCurrentTeam();
	const { teamBreadcrumb } = useEntityBreadcrumbTeam();
	const { user, workspace, isAdminUser } = useAuthUser();

	const { mutateAsync: updateSecodaEntity } = useUpdateSecodaEntity({
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: secodaEntitiesQueryKeyFactory.byId(entity.id),
				});
				// Should be the same key as in action::delete in TableV2.
				// https://github.com/secoda/secoda/blob/8def9a5f50b930955900342ed0918c0df16d6647/apps/frontend/src/components/TableV2/SecodaEntity.hooks.tsx#L898
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.allLists(),
				});
			},
		},
	});

	const { mutateAsync: removeFromTeam } = useApiRemoveEntityFromTeam();

	const navigate = useNavigate();

	const targetTeamId = teamBreadcrumb ?? currentTeam?.id ?? undefined;

	const handleRemoveFromTeam = async () => {
		openDeleteConfirmModal({
			title: 'Remove from team',
			description:
				'This resource is shared across multiple teams. Remove it from the current team only?',
			onConfirm: async () => {
				if (targetTeamId) {
					await removeFromTeam({
						pathParams: { entityId: entity.id },
						body: { team_id: targetTeamId },
					});
				}

				queryClient.invalidateQueries({
					queryKey: catalogQueryKeyFactory.allLists(),
				});
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.allLists(),
				});

				let url = resolveBaseEntityPath(entity.entity_type);
				if (targetTeamId) {
					url = `/teams/${targetTeamId}${url}`;
				}
				navigate(url);
			},
		});
	};

	const handleDeleteFromWorkspace = async () => {
		openDeleteConfirmModal({
			confirmProps: {
				variant: 'primary',
				tone: 'critical',
			},
			title: `Delete ${entity.entity_type}?`,
			description: "You can't undo this action.",
			confirmLabel: `Delete ${entity.entity_type}`,
			onConfirm: async () => {
				await updateSecodaEntity({
					data: {
						id: entity.id,
						archived: true,
					},
				});

				trackEvent(
					`${entity.entity_type}/delete`,
					{
						id: entity.id,
					},
					user,
					workspace
				);

				queryClient.invalidateQueries({
					queryKey: catalogQueryKeyFactory.allLists(),
				});
				queryClient.invalidateQueries({
					queryKey: resourceCatalogQueryKeyFactory.allLists(),
				});

				let url = resolveBaseEntityPath(entity.entity_type);
				if (targetTeamId) {
					url = `/teams/${targetTeamId}${url}`;
				}
				navigate(url);
			},
		});
	};

	const canShowRemoveFromTeam = Boolean(
		targetTeamId &&
			Array.isArray(entity.teams) &&
			entity.teams.includes(targetTeamId) &&
			(entity.teams?.length || 0) > 1
	);

	return (
		<>
			{canShowRemoveFromTeam && (
				<Menu.Item icon={<Icon name="users" />} onClick={handleRemoveFromTeam}>
					Remove from current team...
				</Menu.Item>
			)}
			{isAdminUser && (
				<Menu.Item
					icon={<Icon name="trash" />}
					onClick={handleDeleteFromWorkspace}
					color="red"
				>
					Delete...
				</Menu.Item>
			)}
		</>
	);
}
